<template>
  <div class="w-100 h-100 row mx-0 px-0 align-items-center justify-content-center overflow-auto ">
    <div v-if="!unitsLoaded"
         :class="['omniview-map', `omniview-map-${mapId}`, 'omniviewUnitMap', { isCard }]"></div>
    <div v-else class="col-12 text-center">
      <h2>{{ $tc('messages.loading.title') + " " + $tc('unit.title', 2) + "..." }}</h2>
      <b-spinner style="width: 3rem; height: 3rem;" variant="primary" />
    </div>
  </div>
</template>

<script>
import {Map as OMS} from "@omnicloud/maps-sdk";
import store from "@/store";
import {SLOT_TYPES} from "@/config/layoutConfig";
import layoutMixin from "@/mixins/layoutMixin";
import {v4 as uuidv4} from "uuid";
import LayoutApi from "@/libs/LayoutApi.js";
import useAppConfig from "@core/app-config/useAppConfig";
import {watch} from "@vue/composition-api";
import { getCspNonce } from "@/utils/cspNonce";

const layoutApi = new LayoutApi(store);

let token = window.localStorage.accessToken;

export default {
  mixins: [layoutMixin],
  props: {
    alarm: {
      type: Object,
    },
    units: {
      type: Array
    },
    unit_id: {
      type: String | Number,
    },
    data: {
      type: Object,
      default: null,
    },
    tabInfo: Object,
    isCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mapSelector: null,
      SLOT_TYPES,
      mapId: uuidv4(),
      map: null,
      mapInitScheduled: false,
    };
  },
   computed: {
    unitsLoaded() {
      return store.state.grid.loadingUnits;
    },
  },
  async mounted() {
    if (!this.unitsLoaded) {
      this.scheduleMapInit();
    }

    const {skin} = useAppConfig();
    watch(skin, () => {
      this.scheduleMapInit();
    });
  },
  beforeDestroy() {
    if (this.mapSelector) {
      this.mapSelector.removeEventListener("marker:click", this.onMarkerClick);
      this.mapSelector.removeEventListener("camera:click", this.onCameraClick);
      this.mapSelector.removeEventListener("camera:click-float", this.onCameraClickFloat);
      this.mapSelector.removeEventListener("plane:click", this.onClickPlane);
      this.mapSelector.removeEventListener("circle:updated", this.onCircleUpdated);
      this.mapSelector.removeEventListener("circle:removed", this.onCircleRemoved);
      this.mapSelector.removeEventListener("map:idle", this.onIdle);
    }
  },
  watch: {
    units(value) {
      if (value.length > 0 && !this.unitsLoaded) {
        this.scheduleMapInit();
      }
    },
    unitsLoaded(isLoading) {
      if (!isLoading) {
        this.scheduleMapInit();
      }
    },
    data(value) {
      if (value?.id_unit) {
        this.setMapCenter(value.id_unit, value.zoom);
      }
    },
    '$i18n.locale'(locale) {
      this.map?.setLocale(locale);
    }
  },
  methods: {
    scheduleMapInit() {
      if (this.mapInitScheduled) return;

      this.mapInitScheduled = true;
      this.$nextTick(() => {
        this.mapInitScheduled = false;

        if (!this._isBeingDestroyed && !this._isDestroyed) {
          this.initMap();
        }
      });
    },
    initMap() {
      try {
        const selector = `.omniview-map-${this.mapId}`;
        const mapSelector = document.querySelector(selector);

        if (!mapSelector) return;

        this.mapSelector = mapSelector;
        this.map = new OMS({
          token,
          apiUrl: `${window.__env.VUE_APP_BASE_URL}/v1`,
          locale: this.$i18n.locale,
        });

        const {skin} = useAppConfig();

        let drawingData = {};
        let mapType = 'units';

        if (this.data && this.data.drawingData) {
          drawingData = this.data.drawingData;
          mapType = 'drawing';
        }

        let center = null;

        if (this.data && this.data.centerAuto) {
          if (!this.getGeolocation()) {
            center = {lat: 19.39068, lng: -99.2836975}
          }
        } else {
          center = {lat: 19.39068, lng: -99.2836975}
        }

        let latlng = this.data?.center || center;

        this.map.setConfig({
          container: selector,
          latLng: latlng,
          gmapsKey: window.__env.VUE_APP_GMAPS_KEY,
          triggerEvents: true,
          mode: mapType,
          drawingData,
          locale: this.$i18n.locale,
          zoom: this.data?.zoom || 5,
          darkMode: skin.value === "dark",
          mapId: skin.value === "dark" ? "eac5c4d4f71b58d6" : "SBO",
          nonce: getCspNonce(),
        });

        this.map.onMapReady().then(() => {
          this.map.setMarkers(this.units);
          setTimeout(() => {
            this.setMapCenter();
          }, 1000);
        });

        this.mapSelector.addEventListener("marker:click", this.onMarkerClick);
        this.mapSelector.addEventListener("camera:click", this.onCameraClick);
        this.mapSelector.addEventListener("camera:click-float", this.onCameraClickFloat);
        this.mapSelector.addEventListener("plane:click", this.onClickPlane);
        this.mapSelector.addEventListener("circle:updated", this.onCircleUpdated);
        this.mapSelector.addEventListener("circle:removed", this.onCircleRemoved);
        this.mapSelector.addEventListener("map:idle", this.onIdle);

      } catch (error) {
        console.error("Map|SetConfig", error);
      }
    },
    setMapCenter(unitId = null, zoom = 20) {
      const unit = this.units.find((u) => u.id === (unitId ? unitId : this.unit_id));
      if (unit) {
        this.map.setCenter(
          parseFloat(unit.coordinates.lat),
          parseFloat(unit.coordinates.lng),
          zoom
        );
      }
    },
    onMarkerClick(data) {
      this.$emit('marker:click', {data: data.detail});
    },
    onCameraClick({detail}) {
      const unit = this.units.find((u) => u.id === detail.unit_id);
      const camera = unit.cameras.find((c) => c.id === detail.camera_id);
      setTimeout(() =>
        this.setSlotData(this.SLOT_TYPES.CAM_LIVE, camera.name, {camera})
      );
    },
    onCameraClickFloat({detail}) {
      const unit = this.units.find((u) => u.id === detail.unit_id);
      const camera = unit.cameras.find((c) => c.id === detail.camera_id);
      const uuid = uuidv4();
      this.$store.commit("grid/addFloatVideos", {
        id: camera.camera_id,
        camera_proxy_id: camera.camera_proxy_id,
        type: camera.camera_type,
        uuid,
      });
    },
    onClickPlane({detail}) {
      const unit = this.units.find((u) => u.id === detail.unit_id);
      setTimeout(() =>
        this.setSlotData(this.SLOT_TYPES.PLANE, `plane_${unit.id}`, {
          planes_url: unit.unit_planes,
        })
      );
    },
    onCircleUpdated(event) {
      this.$emit("circle-updated", {action: "updated", data: event.detail});
    },
    onCircleRemoved(event) {
      this.$emit("circle-updated", {action: "removed", data: null});
    },
    onIdle(event) {
      if (this.tabInfo) {
        layoutApi.updateSlotData(this.tabInfo.pos, SLOT_TYPES.UNITS_MAP, {
          data: event.detail,
        });
        //TODO: Check how send the info only when close the view
        store.dispatch("grid/updateUserConfig");
      }
    },
    getGeolocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          let {coords} = position
          this.map.setCenter(coords.latitude, coords.longitude, 13);

          return true;
        }, (error) => {
          let {code} = error

          if (code === 1) {
            console.error('unitsMap: Geolocation permissionDenied');
            return;
          }

          console.error('unitsMap: Geolocation errorGeolocation', error)
        });
      }

      return false;
    },
  },
  errorCaptured(err, vm, info) {
    console.log(err);
  },
};
</script>

<style lang="scss">
.dark-layout .gm-style .gm-style-iw-c {
  background-color: #282f46;
}
</style>

<style lang="scss" scoped>
.omniview-map {
  width: 100%;
  height: 100%;
}

.omniviewUnitMap {
  cursor: pointer;
}

.isCard {
  border-radius: 0.8rem;
}
</style>
